import { type QueryKey, skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { isEqual, isObject } from 'lodash';
import { useMemo } from 'react';

import { Canvases } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { type CanvasListResponse, DEFAULT_CANVASES_ORDER_BY } from '~/swr-hooks/canvases/types';

export const CANVASES_LIST = 'CANVASES_LIST';

export type CanvasesListKey = [typeof CANVASES_LIST, UseListCanvasesParams];

export const getCanvasesListKey = (params: UseListCanvasesParams) => {
  return [CANVASES_LIST, params];
};

export const isCanvasesListKey = (key: QueryKey): key is CanvasesListKey =>
  Array.isArray(key) && key[0] === CANVASES_LIST && isObject(key[1]);

export type UseListCanvasesParams = Omit<NonNullable<Parameters<typeof Canvases.list>[0]>, 'workspaceId'> & {
  workspaceId: string | undefined;
};

type CanvasesListArgs = NonNullable<Parameters<typeof Canvases.list>[0]>;

type ToCanvasesListArgsParams = UseListCanvasesParams & {
  workspaceId: string;
  cursor?: string;
};

/**
 * `Canvases.list` is a discriminated union: `view: 'recent'` forbids `orderBy`. Spreading a
 * flattened `{ view, orderBy }` object does not satisfy that union, so we branch here.
 */
const toCanvasesListArgs = ({ workspaceId, view, orderBy, ...rest }: ToCanvasesListArgsParams): CanvasesListArgs => {
  if (view === 'recent') {
    return { ...rest, workspaceId, view: 'recent' };
  }

  return { ...rest, workspaceId, view, orderBy };
};

export const useListCanvases = (params: UseListCanvasesParams) => {
  const workspaceId = params.workspaceId;

  // The endpoint's own default ordering is `updatedAt desc`, so a caller-supplied default and an
  // omitted `orderBy` are the same request; normalizing them to the same (absent) key keeps callers
  // that don't care about sort sharing a cache entry with the one that explicitly requests the default.
  const isDefaultOrderBy = isEqual(params.orderBy, DEFAULT_CANVASES_ORDER_BY);
  const normalizedParams = isDefaultOrderBy ? { ...params, orderBy: undefined } : params;

  const { data, isLoading, isSuccess, isFetchingNextPage, hasNextPage, fetchNextPage, error } = useInfiniteQuery({
    queryKey: getCanvasesListKey(normalizedParams),
    queryFn: workspaceId
      ? async ({ pageParam }) =>
          callAirApi<CanvasListResponse>(() =>
            Canvases.list(
              toCanvasesListArgs({
                ...normalizedParams,
                workspaceId,
                cursor: pageParam || undefined,
              }),
            ),
          )
      : skipToken,
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor || null,
    initialPageParam: '',
  });

  const flattenedData = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

  const total = data?.pages[0]?.total ?? 0;

  return {
    data: flattenedData,
    total,
    isLoading,
    isSuccess,
    isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    fetchNextPage,
    error,
  };
};
